import React, { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  FiCheck,
  FiChevronDown,
  FiInfo,
  FiMinus,
  FiPlus,
  FiAlertCircle,
  FiClock,
} from 'react-icons/fi';
import { ob, bp } from './theme';

/* ------------------------------------------------------------------ */
/* Layout                                                              */
/* ------------------------------------------------------------------ */

const reveal = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Page = styled.div`
  min-height: 100vh;
  background: ${ob.bg};
  color: ${ob.text};
  font-family: ${ob.fontBody};
  /* clip (not hidden) so position: sticky inside pages keeps working */
  overflow-x: clip;
  -webkit-font-smoothing: antialiased;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  /* themes/global.style.js pins these elements to Poppins; take the flow's own font back. */
  div,
  span,
  p,
  li,
  a,
  input,
  textarea,
  button,
  select,
  label {
    font-family: ${ob.fontBody};
  }
`;

export const Main = styled.main`
  position: relative;
  margin: 0 auto;
  width: 100%;
  max-width: 1100px;
  padding: 16px 16px 12px;
  ${bp.sm} {
    padding: 20px 24px 16px;
  }
`;

export const Content = styled.div<{ $wide?: boolean; $maxWidth?: string; $withPending?: boolean }>`
  margin: 0 auto;
  width: 100%;
  min-width: 0;
  max-width: ${({ $maxWidth, $wide }) => $maxWidth ?? ($wide ? '1024px' : '672px')};
  animation: ${reveal} 320ms ease-out;
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-family: ${ob.fontDisplay};
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: ${ob.text};
  ${bp.sm} {
    font-size: 32px;
    letter-spacing: 0;
  }
`;

export const PageIntro = styled.p`
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.6;
  color: ${ob.muted};
`;

export const Body = styled.div<{ $pending?: boolean }>`
  margin-top: 24px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-bottom: ${({ $pending }) => ($pending ? '300px' : '176px')};
  ${bp.sm} {
    margin-top: 28px;
    padding-bottom: ${({ $pending }) => ($pending ? '224px' : '112px')};
  }
`;

/* ------------------------------------------------------------------ */
/* Header + progress rail + help sheet                                  */
/* ------------------------------------------------------------------ */

export const HeaderBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 40;
  border-bottom: 1px solid rgba(238, 235, 230, 0.7);
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(10px);
`;

export const HeaderInner = styled.div`
  margin: 0 auto;
  display: flex;
  height: 56px;
  max-width: 1240px;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  ${bp.sm} {
    height: 72px;
  }
  ${bp.md} {
    padding: 0 32px;
  }
`;

export const LogoButton = styled.button`
  display: inline-flex;
  align-items: center;
  margin-left: -4px;
  padding: 6px 4px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  transition: background 140ms ease;
  &:hover {
    background: rgba(26, 26, 26, 0.05);
  }
  &:focus-visible {
    outline: 2px solid ${ob.primary};
    outline-offset: 4px;
  }
  img {
    display: block;
    height: 24px;
    width: auto;
    ${bp.sm} {
      height: 28px;
    }
  }
`;

export const HelpButton = styled.button`
  display: inline-flex;
  height: 36px;
  align-items: center;
  gap: 8px;
  padding: 0 14px 0 12px;
  border: 1px solid rgba(255, 113, 15, 0.3);
  border-radius: 999px;
  background: rgba(255, 240, 230, 0.6);
  color: ${ob.text};
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(26, 26, 26, 0.05);
  transition: all 140ms ease;
  &:hover {
    border-color: rgba(255, 113, 15, 0.5);
    background: ${ob.primarySoft};
  }
  &:focus-visible {
    outline: 2px solid ${ob.primary};
    outline-offset: 4px;
  }
  svg {
    color: ${ob.primary};
  }
`;

const ping = keyframes`
  75%, 100% { transform: scale(2.2); opacity: 0; }
`;

export const LiveDot = styled.span`
  position: relative;
  display: inline-flex;
  width: 6px;
  height: 6px;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: rgba(16, 185, 129, 0.7);
    animation: ${ping} 1.4s cubic-bezier(0, 0, 0.2, 1) infinite;
  }
  &::after {
    content: '';
    position: relative;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #10b981;
  }
`;

const HideOnMobile = styled.span`
  display: none;
  ${bp.sm} {
    display: inline;
  }
`;
const ShowOnMobile = styled.span`
  ${bp.sm} {
    display: none;
  }
`;

export const HelpLabel = () => (
  <>
    <HideOnMobile>Need Help?</HideOnMobile>
    <ShowOnMobile>Help</ShowOnMobile>
  </>
);

const RailWrap = styled.div`
  margin: 0 auto;
  max-width: 1100px;
  padding: 16px 16px 0;
  ${bp.sm} {
    padding: 20px 24px 0;
  }
`;

const RailLabels = styled.div`
  display: none;
  gap: 4px;
  ${bp.sm} {
    display: flex;
  }
`;

const RailLabel = styled.span<{ $state: 'current' | 'done' | 'todo' }>`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  font-size: 10px;
  line-height: 1.3;
  transition: color 300ms ease;
  color: ${({ $state }) =>
    $state === 'current'
      ? ob.text
      : $state === 'done'
        ? 'rgba(107,107,107,0.8)'
        : 'rgba(107,107,107,0.5)'};
  font-weight: ${({ $state }) => ($state === 'current' ? 600 : 400)};
`;

const RailMobile = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: baseline;
  gap: 12px;
  ${bp.sm} {
    display: none;
  }
  span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    font-weight: 600;
  }
  span:last-child {
    font-size: 11px;
    font-weight: 500;
    color: ${ob.muted};
  }
`;

const RailBars = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RailBar = styled.span<{ $on: boolean }>`
  height: 6px;
  flex: 1;
  border-radius: 999px;
  transition: background 300ms ease;
  background: ${({ $on }) => ($on ? ob.primary : ob.mutedBg)};
`;

/** `active` is the first highlighted section; `through` (optional) extends the highlight for pages that cover two sections. */
export const ProgressRail: React.FC<{
  items: readonly string[];
  active: number;
  through?: number;
}> = ({ items, active, through }) => {
  const last = through ?? active;
  const state = (i: number): 'current' | 'done' | 'todo' =>
    i >= active && i <= last ? 'current' : i < active ? 'done' : 'todo';
  return (
    <RailWrap>
      <RailLabels aria-hidden="true">
        {items.map((label, i) => (
          <RailLabel key={label} $state={state(i)}>
            {label}
          </RailLabel>
        ))}
      </RailLabels>
      <RailMobile>
        <span>{items[active]}</span>
        <span>
          Step {active + 1} of {items.length}
        </span>
      </RailMobile>
      <RailBars
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={items.length}
        aria-valuenow={last + 1}
        aria-label="Listing progress"
      >
        {items.map((label, i) => (
          <RailBar key={label} title={label} $on={i <= last} />
        ))}
      </RailBars>
    </RailWrap>
  );
};

/* ------------------------------------------------------------------ */
/* Sticky action bar                                                   */
/* ------------------------------------------------------------------ */

export const ActionBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;
  border-top: 1px solid ${ob.border};
  background: #fff;
  box-shadow: 0 -6px 20px -18px rgba(0, 0, 0, 0.5);
`;

export const ActionBarInner = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: 768px;
  padding: 10px 16px max(10px, env(safe-area-inset-bottom));
  ${bp.sm} {
    padding-left: 24px;
    padding-right: 24px;
  }
`;

export const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const PendingBox = styled.div`
  margin-bottom: 8px;
  max-height: 112px;
  overflow-y: auto;
  border-bottom: 1px solid rgba(238, 235, 230, 0.7);
  padding-bottom: 8px;
  font-size: 12px;
  line-height: 1.5;
  color: ${ob.muted};
  p {
    margin: 0;
    font-weight: 600;
    color: ${ob.text};
  }
  ul {
    margin: 2px 0 0;
    padding: 0;
    list-style: none;
    display: grid;
    column-gap: 16px;
    ${bp.sm} {
      grid-template-columns: 1fr 1fr;
    }
  }
  li {
    display: flex;
    align-items: flex-start;
    gap: 6px;
  }
  svg {
    margin-top: 3px;
    flex-shrink: 0;
    color: ${ob.warning};
  }
`;

const btnBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  border-radius: 999px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 140ms ease;
  &:active:not(:disabled) {
    transform: scale(0.97);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  &:focus-visible {
    outline: 2px solid ${ob.primary};
    outline-offset: 3px;
  }
`;

export const PrimaryButton = styled.button<{ $block?: boolean }>`
  ${btnBase}
  padding: 0 22px;
  border: 0;
  background: ${ob.primary};
  color: ${ob.primaryText};
  box-shadow: ${ob.shadowBrand};
  width: ${({ $block }) => ($block ? '100%' : 'auto')};
  &:hover:not(:disabled) {
    filter: brightness(0.96);
  }
`;

export const GhostButton = styled.button`
  ${btnBase}
  padding: 0 14px;
  border: 0;
  background: transparent;
  color: ${ob.text};
  &:hover:not(:disabled) {
    background: rgba(26, 26, 26, 0.05);
  }
`;

export const OutlineButton = styled.button<{ $block?: boolean }>`
  ${btnBase}
  padding: 0 18px;
  border: 1px solid ${ob.borderStrong};
  background: #fff;
  color: ${ob.text};
  width: ${({ $block }) => ($block ? '100%' : 'auto')};
  &:hover:not(:disabled) {
    border-color: rgba(255, 113, 15, 0.45);
    background: ${ob.primarySoft};
  }
`;

/* ------------------------------------------------------------------ */
/* Form primitives                                                     */
/* ------------------------------------------------------------------ */

const inputBase = css<{ $invalid?: boolean }>`
  width: 100%;
  margin-top: 8px;
  border: 1px solid ${({ $invalid }) => ($invalid ? ob.danger : ob.border)};
  border-radius: 12px;
  background: #fff;
  color: ${ob.text};
  font-family: inherit;
  font-size: 14px;
  box-shadow: 0 1px 1px rgba(26, 26, 26, 0.03);
  transition:
    border-color 140ms ease,
    box-shadow 140ms ease;
  &::placeholder {
    color: ${ob.muted};
    opacity: 0.8;
  }
  &:hover:not(:disabled) {
    border-color: ${({ $invalid }) => ($invalid ? ob.danger : 'rgba(255,113,15,0.35)')};
  }
  &:focus {
    outline: none;
    border-color: ${({ $invalid }) => ($invalid ? ob.danger : ob.primary)};
    box-shadow: 0 0 0 3px ${ob.ring};
  }
  &:disabled {
    background: ${ob.surface};
    color: ${ob.muted};
    cursor: not-allowed;
  }
`;

export const TextInput = styled.input<{ $invalid?: boolean }>`
  ${inputBase}
  height: 48px;
  padding: 0 14px;
`;

export const TextArea = styled.textarea<{ $invalid?: boolean }>`
  ${inputBase}
  min-height: 140px;
  padding: 12px 14px;
  line-height: 1.6;
  resize: vertical;
`;

export const SelectInput = styled.select<{ $invalid?: boolean }>`
  ${inputBase}
  height: 48px;
  padding: 0 14px;
`;

export const FieldWrap = styled.div`
  min-width: 0;
`;

export const FieldLabel = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: ${ob.text};
`;

export const Required = styled.span`
  color: ${ob.danger};
`;

export const Hint = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${ob.muted};
`;

export const ErrorText = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  font-weight: 500;
  color: ${ob.danger};
`;

export const WarnText = styled.p`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 8px 0 0;
  font-size: 12px;
  font-weight: 500;
  color: ${ob.warning};
`;

const TipWrap = styled.span`
  position: relative;
  display: inline-flex;
`;

const TipTrigger = styled.span`
  display: flex;
  align-items: center;
  margin: -4px;
  padding: 4px;
  border-radius: 50%;
  color: ${ob.muted};
  cursor: help;
  &:hover,
  &:focus-visible {
    color: ${ob.text};
    outline: none;
  }
`;

const TipBubble = styled.span<{ $open: boolean }>`
  position: absolute;
  z-index: 30;
  bottom: 100%;
  left: -8px;
  margin-bottom: 8px;
  width: 224px;
  max-width: min(14rem, calc(100vw - 2.5rem));
  padding: 12px 16px;
  border: 1px solid ${ob.border};
  border-radius: 12px;
  background: #fff;
  color: ${ob.text};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  box-shadow: 0 8px 24px rgba(26, 26, 26, 0.12);
  pointer-events: none;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity 150ms ease;
  ${TipWrap}:hover &,
  ${TipWrap}:focus-within & {
    opacity: 1;
  }
`;

export const InfoTip: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  return (
    <TipWrap ref={ref} onClick={(e) => e.stopPropagation()}>
      <TipTrigger
        role="button"
        tabIndex={0}
        aria-label={`About ${label}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <FiInfo size={16} />
      </TipTrigger>
      <TipBubble role="tooltip" $open={open}>
        {children}
      </TipBubble>
    </TipWrap>
  );
};

export const Field: React.FC<{
  label: string;
  hint?: string;
  info?: React.ReactNode;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}> = ({ label, hint, info, required, error, children }) => (
  <FieldWrap>
    <FieldLabel>
      {label}
      {required ? <Required aria-hidden="true">*</Required> : null}
      {info ? <InfoTip label={label}>{info}</InfoTip> : null}
    </FieldLabel>
    {children}
    {hint ? <Hint>{hint}</Hint> : null}
    {error ? <ErrorText role="alert">{error}</ErrorText> : null}
  </FieldWrap>
);

export const RequiredChoice: React.FC<{ message: string }> = ({ message }) => (
  <WarnText role="status">
    <FiAlertCircle size={14} />
    {message}
  </WarnText>
);

const GroupTitle = styled.h2<{ $large?: boolean }>`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${ob.fontDisplay};
  font-size: ${({ $large }) => ($large ? '22px' : '15px')};
  font-weight: 600;
  color: ${ob.text};
`;

const GroupNote = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${ob.muted};
`;

const GroupBody = styled.div<{ $bare?: boolean; $dense?: boolean; $titled: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ $bare, $dense, $titled }) =>
    $bare || !$titled
      ? ''
      : `margin-top: ${$dense ? 8 : 12}px; padding-top: ${$dense ? 12 : 16}px; border-top: 1px solid rgba(238,235,230,0.7);`}
`;

export const Group: React.FC<{
  title?: string;
  note?: string;
  info?: React.ReactNode;
  dense?: boolean;
  bare?: boolean;
  /** A bigger heading, for a section that opens a new part of the page. */
  largeTitle?: boolean;
  children: React.ReactNode;
}> = ({ title, note, info, dense, bare, largeTitle, children }) => (
  <div>
    {title ? (
      <GroupTitle $large={largeTitle}>
        {title}
        {info ? <InfoTip label={title}>{info}</InfoTip> : null}
      </GroupTitle>
    ) : null}
    {note ? <GroupNote>{note}</GroupNote> : null}
    <GroupBody $bare={bare} $dense={dense} $titled={!!title}>
      {children}
    </GroupBody>
  </div>
);

export const Grid = styled.div<{ $cols?: number; $gap?: number }>`
  display: grid;
  gap: ${({ $gap }) => $gap ?? 12}px;
  ${bp.sm} {
    grid-template-columns: ${({ $cols }) => `repeat(${$cols ?? 2}, minmax(0, 1fr))`};
  }
`;

export const Panel = styled.section`
  overflow: hidden;
  border: 1px solid ${ob.border};
  border-radius: 16px;
  background: ${ob.card};
  box-shadow: ${ob.shadowCard};
`;

export const SoftCard = styled.section`
  border: 1px solid ${ob.border};
  border-radius: 16px;
  background: ${ob.card};
  padding: 16px;
  box-shadow: 0 1px 2px rgba(26, 26, 26, 0.04);
`;

export const InfoBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 1px solid rgba(255, 113, 15, 0.25);
  border-radius: 12px;
  background: rgba(255, 240, 230, 0.5);
  font-size: 13px;
  line-height: 1.6;
  svg {
    margin-top: 2px;
    flex-shrink: 0;
    color: ${ob.primary};
  }
`;

/* ------------------------------------------------------------------ */
/* Selection controls                                                  */
/* ------------------------------------------------------------------ */

export const Chip = styled.button<{ $active?: boolean; $soft?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 8px 16px;
  border-radius: 999px;
  border: 1px solid
    ${({ $active, $soft }) => ($active ? ob.primary : $soft ? ob.border : ob.border)};
  background: ${({ $active, $soft }) =>
    $active ? ($soft ? ob.primarySoft : ob.primary) : ob.card};
  color: ${({ $active, $soft }) =>
    $active ? ($soft ? ob.text : ob.primaryText) : ob.muted};
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 140ms ease;
  &:hover {
    border-color: rgba(255, 113, 15, 0.4);
    color: ${({ $active, $soft }) => ($active && !$soft ? ob.primaryText : ob.text)};
  }
  &:active {
    transform: scale(0.97);
  }
`;

export const ChipRow = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const Chips: React.FC<{
  options: string[];
  value: string;
  onChange: (v: string) => void;
}> = ({ options, value, onChange }) => (
  <ChipRow>
    {options.map((item) => (
      <Chip
        key={item}
        type="button"
        aria-pressed={value === item}
        $active={value === item}
        onClick={() => onChange(item)}
      >
        {value === item ? <FiCheck size={14} /> : null}
        {item}
      </Chip>
    ))}
  </ChipRow>
);

export const MultiChips: React.FC<{
  options: string[];
  values: string[];
  onToggle: (v: string) => void;
}> = ({ options, values, onToggle }) => (
  <ChipRow>
    {options.map((item) => {
      const active = values.includes(item);
      return (
        <Chip
          key={item}
          type="button"
          aria-pressed={active}
          $active={active}
          onClick={() => onToggle(item)}
        >
          {active ? <FiCheck size={14} /> : null}
          {item}
        </Chip>
      );
    })}
  </ChipRow>
);

const CounterBox = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${ob.border};
  border-radius: 12px;
  background: ${ob.card};
  ${bp.sm} {
    padding: 12px 16px;
  }
  > span:first-child {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
  }
`;

const CounterControls = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  ${bp.sm} {
    gap: 12px;
  }
  .val {
    width: 20px;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;

export const RoundIconButton = styled.button`
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border: 1px solid ${ob.borderStrong};
  border-radius: 50%;
  background: #fff;
  color: ${ob.text};
  cursor: pointer;
  transition: all 140ms ease;
  &:hover:not(:disabled) {
    border-color: ${ob.primary};
    color: ${ob.primary};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const Counter: React.FC<{
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  info?: string;
}> = ({ label, value, onChange, min = 0, max, info }) => (
  <CounterBox>
    <span>
      {label}
      {info ? <InfoTip label={label}>{info}</InfoTip> : null}
    </span>
    <CounterControls>
      <RoundIconButton
        type="button"
        aria-label={`Decrease ${label}`}
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
      >
        <FiMinus size={16} />
      </RoundIconButton>
      <span className="val">{value}</span>
      <RoundIconButton
        type="button"
        aria-label={`Increase ${label}`}
        disabled={max !== undefined && value >= max}
        onClick={() => onChange(max !== undefined ? Math.min(max, value + 1) : value + 1)}
      >
        <FiPlus size={16} />
      </RoundIconButton>
    </CounterControls>
  </CounterBox>
);

const SwitchButton = styled.button`
  display: grid;
  width: 100%;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid ${ob.border};
  border-radius: 12px;
  background: ${ob.card};
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  ${bp.sm} {
    gap: 16px;
    padding: 12px 16px;
  }
  .label {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 500;
    color: ${ob.text};
  }
  .note {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    line-height: 1.5;
    color: ${ob.muted};
  }
`;

const Track = styled.span<{ $on: boolean }>`
  position: relative;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 999px;
  background: ${({ $on }) => ($on ? ob.primary : ob.mutedBg)};
  transition: background 160ms ease;
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${({ $on }) => ($on ? '22px' : '2px')};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    transition: left 160ms ease;
  }
`;

export const SwitchRow: React.FC<{
  label: string;
  note?: string;
  info?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, note, info, checked, onChange }) => (
  <SwitchButton type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}>
    <span>
      <span className="label">
        {label}
        {info ? <InfoTip label={label}>{info}</InfoTip> : null}
      </span>
      {note ? <span className="note">{note}</span> : null}
    </span>
    <Track $on={checked} />
  </SwitchButton>
);

/** Large selectable card with a leading image/icon, used for property type and category pickers. */
export const SelectCard = styled.div<{ $selected?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 18px 12px;
  border: 1px solid ${({ $selected }) => ($selected ? ob.primary : ob.border)};
  border-radius: 16px;
  background: ${({ $selected }) => ($selected ? ob.primarySoft : ob.card)};
  box-shadow: ${({ $selected }) => ($selected ? ob.shadowBrand : 'none')};
  text-align: center;
  cursor: pointer;
  transition: all 160ms ease;
  &:hover {
    border-color: rgba(255, 113, 15, 0.5);
    transform: translateY(-1px);
  }
  img {
    width: 48px;
    height: 48px;
    object-fit: contain;
  }
  p,
  h2,
  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${ob.text};
  }
`;

export const RadioCard = styled.button<{ $active?: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid ${({ $active }) => ($active ? ob.primary : ob.border)};
  border-radius: 12px;
  background: ${ob.card};
  box-shadow: ${({ $active }) => ($active ? ob.shadowBrand : 'none')};
  color: ${({ $active }) => ($active ? ob.text : ob.muted)};
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: all 140ms ease;
  &:hover {
    border-color: rgba(255, 113, 15, 0.4);
    color: ${ob.text};
  }
`;

export const RadioDot = styled.span<{ $active?: boolean }>`
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid ${({ $active }) => ($active ? ob.primary : ob.borderStrong)};
  background: ${({ $active }) => ($active ? ob.primary : 'transparent')};
  color: #fff;
`;

const DisclosureBox = styled.div`
  overflow: hidden;
  border: 1px solid ${ob.border};
  border-radius: 12px;
  background: ${ob.card};
  > button {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border: 0;
    background: transparent;
    font-family: inherit;
    text-align: left;
    cursor: pointer;
    .title {
      display: block;
      font-size: 14px;
      font-weight: 600;
    }
    .note {
      display: block;
      margin-top: 2px;
      font-size: 12px;
      color: ${ob.muted};
    }
    svg {
      flex-shrink: 0;
      color: ${ob.muted};
      transition: transform 160ms ease;
    }
    &[aria-expanded='true'] svg {
      transform: rotate(180deg);
    }
  }
  .panel {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    border-top: 1px solid ${ob.border};
  }
`;

export const Disclosure: React.FC<{
  title: string;
  note?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, note, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <DisclosureBox>
      <button type="button" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span>
          <span className="title">{title}</span>
          {note ? <span className="note">{note}</span> : null}
        </span>
        <FiChevronDown size={16} />
      </button>
      {open ? <div className="panel">{children}</div> : null}
    </DisclosureBox>
  );
};

/** Inline money / suffix input shell, e.g. "₹ [ 4500 ]". */
export const AdornedInput = styled.div`
  position: relative;
  margin-top: 8px;
  .pre,
  .post {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    font-size: 14px;
    font-weight: 600;
    color: ${ob.muted};
    pointer-events: none;
  }
  .pre {
    left: 16px;
  }
  .post {
    right: 16px;
    font-weight: 500;
  }
  input {
    margin-top: 0;
  }
`;

export const Divider = styled.hr`
  margin: 0;
  border: 0;
  border-top: 1px solid ${ob.border};
`;

export const Spinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 113, 15, 0.25);
  border-top-color: ${ob.primary};
  border-radius: 50%;
  animation: ${keyframes`to { transform: rotate(360deg); }`} 0.7s linear infinite;
`;

/* ------------------------------------------------------------------ */
/* Time picker (hour / minute / AM-PM columns)                         */
/* ------------------------------------------------------------------ */

const TIME_HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const TIME_MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
const TIME_PERIODS = ['AM', 'PM'];

const splitTime = (value: string) => {
  if (!/^\d{1,2}:\d{2}/.test(value)) return null;
  const [rawHour = '12', rawMinute = '00'] = value.split(':');
  const hour24 = Number(rawHour);
  const period = hour24 >= 12 ? 'PM' : 'AM';
  let hour12 = hour24 % 12;
  if (hour12 === 0) hour12 = 12;
  return {
    hour: String(hour12).padStart(2, '0'),
    minute: rawMinute.padStart(2, '0').slice(0, 2),
    period,
  };
};

const joinTime = (hour: string, minute: string, period: string) => {
  let h = Number(hour) % 12;
  if (period === 'PM') h += 12;
  return `${String(h).padStart(2, '0')}:${minute}`;
};

const TimeRoot = styled.div`
  position: relative;
  margin-top: 8px;
`;

const TimeTrigger = styled.button<{ $filled: boolean }>`
  display: flex;
  height: 48px;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
  border: 1px solid ${ob.border};
  border-radius: 12px;
  background: ${ob.surface};
  color: ${({ $filled }) => ($filled ? ob.text : ob.muted)};
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 1px 1px rgba(26, 26, 26, 0.03);
  transition: all 140ms ease;
  &:hover {
    border-color: rgba(255, 113, 15, 0.35);
  }
  &:focus-visible {
    outline: none;
    border-color: ${ob.primary};
    box-shadow: 0 0 0 3px ${ob.ring};
  }
`;

const TimePanel = styled.div<{ $up: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  z-index: 40;
  display: flex;
  overflow: hidden;
  border: 1px solid ${ob.border};
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 18px 40px -24px rgba(0, 0, 0, 0.45);
  ${({ $up }) => ($up ? 'bottom: 100%; margin-bottom: 8px;' : 'top: 100%; margin-top: 8px;')}
`;

const TimeColumnList = styled.div`
  max-height: 208px;
  flex: 1;
  overflow-y: auto;
  border-right: 1px solid rgba(238, 235, 230, 0.7);
  &:last-child {
    border-right: 0;
  }
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const TimeOption = styled.button<{ $selected: boolean }>`
  display: block;
  width: 100%;
  padding: 8px 12px;
  border: 0;
  background: ${({ $selected }) => ($selected ? ob.primary : 'transparent')};
  color: ${({ $selected }) => ($selected ? '#fff' : ob.text)};
  font-family: inherit;
  font-size: 14px;
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  text-align: center;
  cursor: pointer;
  &:hover {
    background: ${({ $selected }) => ($selected ? ob.primary : 'rgba(255,240,230,0.6)')};
  }
`;

export const TimePicker: React.FC<{
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  placeholder?: string;
}> = ({ value, onChange, ariaLabel, placeholder = 'Select time' }) => {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const time = splitTime(value);
  const current = time ?? { hour: '12', minute: '00', period: 'PM' };
  const apply = (hour: string, minute: string, period: string) =>
    onChange(joinTime(hour, minute, period));

  const toggle = () => {
    setOpen((cur) => {
      const next = !cur;
      if (next && rootRef.current) {
        const rect = rootRef.current.getBoundingClientRect();
        setDropUp(window.innerHeight - rect.bottom < 300 && rect.top > 240);
      }
      return next;
    });
  };

  const column = (options: string[], selected: string, onSelect: (v: string) => void, label: string) => (
    <TimeColumnList role="listbox" aria-label={`${ariaLabel} ${label}`}>
      {options.map((option) => (
        <TimeOption
          key={option}
          type="button"
          role="option"
          aria-selected={option === selected}
          $selected={!!time && option === selected}
          onClick={() => onSelect(option)}
        >
          {option}
        </TimeOption>
      ))}
    </TimeColumnList>
  );

  return (
    <TimeRoot ref={rootRef}>
      <TimeTrigger type="button" aria-label={ariaLabel} aria-expanded={open} $filled={!!time} onClick={toggle}>
        <span>{time ? `${time.hour}:${time.minute} ${time.period}` : placeholder}</span>
        <FiClock size={16} color={ob.muted} />
      </TimeTrigger>
      {open ? (
        <TimePanel $up={dropUp}>
          {column(TIME_HOURS, current.hour, (h) => apply(h, current.minute, current.period), 'hour')}
          {column(TIME_MINUTES, current.minute, (m) => apply(current.hour, m, current.period), 'minute')}
          {column(TIME_PERIODS, current.period, (p) => apply(current.hour, current.minute, p), 'AM or PM')}
        </TimePanel>
      ) : null}
    </TimeRoot>
  );
};
